import React from "react";
import { UserCard } from "./UserCard";

/**
 * CSS styles for grid-based gallery with uniform card heights
 */
export const masonryStyles = `
  .user-gallery-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 0.5rem;
  }

  .gallery-card {
    height: 150px;
  }

  @media (min-width: 768px) {
    .user-gallery-grid {
      grid-template-columns: repeat(4, 1fr);
      gap: 0.75rem;
    }
    .gallery-card {
      height: 180px;
    }
  }

  @media (min-width: 1024px) {
    .user-gallery-grid {
      grid-template-columns: repeat(5, 1fr);
      gap: 1rem;
    }
    .gallery-card {
      height: 200px;
    }
  }

  @media (min-width: 1280px) {
    .user-gallery-grid {
      grid-template-columns: repeat(6, 1fr);
    }
    .gallery-card {
      height: 220px;
    }
  }

  @media (min-width: 1536px) {
    .user-gallery-grid {
      grid-template-columns: repeat(8, 1fr);
    }
  }

  /* Compact size variant */
  .gallery-card-compact {
    height: 100px;
  }

  @media (min-width: 768px) {
    .gallery-card-compact {
      height: 120px;
    }
  }

  @media (min-width: 1024px) {
    .gallery-card-compact {
      height: 140px;
    }
  }

  @media (min-width: 1280px) {
    .gallery-card-compact {
      height: 150px;
    }
  }
`;

/**
 * UserMasonryGallery component for displaying users in a masonry grid layout
 *
 * @param {Object} props
 * @param {Array} props.users - Array of users to display
 * @param {boolean} props.isSelectionMode - Whether selection mode is active
 * @param {Function} props.isUserSelected - Function to check if a user is selected
 * @param {Function} props.onUserClick - Handler when a user card is clicked
 * @param {Function} props.onExpandPhoto - Handler to expand a user's photo (optional)
 * @param {React.Ref} props.loadMoreRef - Ref for infinite scroll trigger element
 * @param {boolean} props.isLoadingMore - Whether more users are being loaded
 * @param {boolean} props.hasMore - Whether there are more users to load
 * @param {number} props.totalCount - Total number of users (for "all loaded" message)
 * @param {boolean} props.compact - Whether to use compact (smaller) card sizes
 */
export function UserMasonryGallery({
  users,
  isSelectionMode,
  isUserSelected,
  onUserClick,
  onExpandPhoto,
  loadMoreRef,
  isLoadingMore,
  hasMore,
  totalCount,
  compact = false,
}) {
  return (
    <div className="mt-8">
      <style>{masonryStyles}</style>
      <div className="user-gallery-grid">
        {users.map((user) => (
          <UserCard
            key={user.id}
            user={user}
            isSelectionMode={isSelectionMode}
            isSelected={isUserSelected(user.id)}
            onClick={() => onUserClick(user)}
            onExpand={onExpandPhoto}
            compact={compact}
          />
        ))}
      </div>

      {/* Infinite scroll trigger and loading indicator */}
      {loadMoreRef && (
        <div ref={loadMoreRef} className="w-full py-8 flex justify-center">
          {isLoadingMore && (
            <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400">
              <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                  fill="none"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              <span>Loading more...</span>
            </div>
          )}
          {!hasMore && users.length > 0 && (
            <p className="text-gray-400 dark:text-gray-500 text-sm">
              All {totalCount || users.length} users loaded
            </p>
          )}
        </div>
      )}
    </div>
  );
}
